import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const BudgetManager = () => {
  const [budget, setBudget] = useState(null);
  const [maxCapPercentage, setMaxCapPercentage] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Map rating (1-5) to score range for local GRID lookup
  const ratingToScoreRange = (rating) => {
    if (rating >= 4.5) return '93-100';
    if (rating >= 3.5) return '88-92';
    if (rating >= 2.5) return '82-87';
    if (rating >= 1.5) return '76-81';
    return '51-75';
  };

  // Local GRID lookup (mirrors GRID sheet "Appraisal Grid" tab)
  const getGridSuggestedPercent = (band, monthlyCTC, rating) => {
    if (!rating || rating <= 0) return 0;
    const scoreRange = ratingToScoreRange(rating);

    const grid = {
      individualContributor: [
        { slab: 'Upto 20000', min: 0, max: 20000, '51-75': 0, '76-81': 8, '82-87': 10, '88-92': 12, '93-100': 14 },
        { slab: '20000 to 30000', min: 20000, max: 30000, '51-75': 0, '76-81': 7, '82-87': 9, '88-92': 11, '93-100': 13 },
        { slab: '30001 to 40000', min: 30001, max: 40000, '51-75': 0, '76-81': 6, '82-87': 8, '88-92': 10, '93-100': 12 },
        { slab: '40001 to 50000', min: 40001, max: 50000, '51-75': 0, '76-81': 5, '82-87': 7, '88-92': 9, '93-100': 11 },
        { slab: '50001 to 60000', min: 50001, max: 60000, '51-75': 0, '76-81': 3, '82-87': 4, '88-92': 5, '93-100': 7 },
        { slab: 'Above 60000', min: 60001, max: Infinity, '51-75': 0, '76-81': 2, '82-87': 3, '88-92': 4, '93-100': 6 },
      ],
      middleManagement: [
        { slab: '40001 to 50000', min: 40001, max: 50000, '51-75': 0, '76-81': 5, '82-87': 7, '88-92': 9, '93-100': 11 },
        { slab: '50001 to 60000', min: 50001, max: 60000, '51-75': 0, '76-81': 4, '82-87': 6, '88-92': 8, '93-100': 10 },
        { slab: 'Above 60000', min: 60001, max: Infinity, '51-75': 0, '76-81': 3, '82-87': 4, '88-92': 5, '93-100': 6 },
      ],
      seniorManagement: [
        { slab: 'NA', min: 0, max: Infinity, '51-75': 0, '76-81': 7, '82-87': 9, '88-92': 12, '93-100': 15 },
      ],
    };

    const bandLower = (band || '').toLowerCase();
    let table;
    if (bandLower.includes('middle management') || bandLower.includes('team management')) table = grid.middleManagement;
    else if (bandLower.includes('senior management')) table = grid.seniorManagement;
    else table = grid.individualContributor;

    for (const row of table) {
      if (monthlyCTC >= row.min && monthlyCTC <= row.max) {
        return row[scoreRange] || 0;
      }
    }
    const lastRow = table[table.length - 1];
    return lastRow[scoreRange] || 0;
  };

  useEffect(() => {
    initializeData();
  }, []);

  const initializeData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch budget config, employees, and all ratings in parallel
      const [budgetData, employeesData, allRatings] = await Promise.all([
        api.get('/api/budget'),
        api.get('/api/employees'),
        api.get('/api/all-ratings').catch(() => []),  // graceful fallback
      ]);

      const totalBudget = budgetData.totalBudget || budgetData.overallBudget || 2500000;
      const maxCap = budgetData.maxCapPercentage || 25;
      setBudget(totalBudget);
      setMaxCapPercentage(maxCap);

      // Build ratings lookup map by employeeId
      const ratingsMap = {};
      allRatings.forEach(r => {
        ratingsMap[r.employeeId] = r;
      });

      // Merge employees with their ratings and compute GRID suggested %
      const allocWithDetails = employeesData.map(emp => {
        const empId = emp.empID || emp.id;
        const ratingData = ratingsMap[empId];
        const currentSalary = emp.salary || 0;
        const band = emp.band || '';
        const rating = ratingData ? parseFloat(ratingData.overallRating) || 0 : 0;
        const gridPercent = getGridSuggestedPercent(band, currentSalary, rating);
        const suggestedPercent = Math.min(gridPercent, maxCap);

        return {
          ...emp,
          currentSalary,
          band,
          rating,
          suggestedPercent,
          gridPercent,
          isRated: !!ratingData,
        };
      });

      console.log('[BudgetManager] Sample employee:', allocWithDetails[0]);
      console.log('[BudgetManager] Ratings found:', allRatings.length, 'Employees:', employeesData.length);
      setAllocations(allocWithDetails);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to load budget and employee data');
      setAllocations([]);
    }
    setLoading(false);
  };

  // Update a single employee's rating
  const handleRatingChange = (empId, newRating) => {
    const parsed = parseFloat(newRating);
    if (isNaN(parsed) || parsed < 0 || parsed > 5) return;
    setAllocations(prev => prev.map(a => {
      if (a.id !== empId) return a;
      const gridPercent = getGridSuggestedPercent(a.band, a.currentSalary, parsed);
      const suggestedPercent = Math.min(gridPercent, maxCapPercentage);
      return { ...a, rating: parsed, suggestedPercent, gridPercent };
    }));
  };

  // Update a single employee's suggested %
  const handleSuggestedPercentChange = (empId, newPercent) => {
    const parsed = parseFloat(newPercent);
    if (isNaN(parsed) || parsed < 0) return;
    const capped = Math.min(parsed, maxCapPercentage);
    setAllocations(prev => prev.map(a =>
      a.id === empId ? { ...a, suggestedPercent: capped } : a
    ));
  };

  const handleBudgetChange = (newBudget) => {
    setBudget(newBudget);
    saveBudgetConfig(newBudget, maxCapPercentage);
  };

  const handleMaxCapChange = (newMaxCap) => {
    setMaxCapPercentage(newMaxCap);
    // Clamp all existing suggested % to new max cap
    setAllocations(prev => prev.map(a => ({
      ...a,
      suggestedPercent: Math.min(a.suggestedPercent, newMaxCap),
    })));
    saveBudgetConfig(budget, newMaxCap);
  };

  const saveBudgetConfig = async (totalBudget, maxCap) => {
    try {
      await api.post('/api/budget', { totalBudget, maxCapPercentage: maxCap });
    } catch (err) {
      console.error('Error saving budget config:', err);
    }
  };

  // Download salary revision sheet as CSV
  const downloadSalarySheet = () => {
    const headers = [
      'Emp ID',
      'Employee Name',
      'Designation',
      'Band',
      'Category',
      'Rating',
      'Score Range',
      'Current Monthly Salary (INR)',
      'Current Annual Salary (INR)',
      'Grid %',
      'Suggested %',
      'Monthly Increment (INR)',
      'New Monthly Salary (INR)',
      'New Annual Salary (INR)',
    ];

    const rows = computedAllocations.map(a => {
      const annualOld = a.currentSalary * 12;
      const annualNew = a.newSalary * 12;
      let scoreRange = '';
      if (a.rating >= 4.5) scoreRange = '93-100';
      else if (a.rating >= 3.5) scoreRange = '88-92';
      else if (a.rating >= 2.5) scoreRange = '82-87';
      else if (a.rating >= 1.5) scoreRange = '76-81';
      else if (a.rating > 0) scoreRange = '51-75';

      return [
        a.empID || a.id,
        a.name,
        a.designation,
        a.band || '',
        a.category || '',
        a.rating || 0,
        scoreRange,
        a.currentSalary,
        annualOld,
        a.gridPercent || 0,
        a.suggestedPercent || 0,
        a.incrementAmount,
        a.newSalary,
        annualNew,
      ];
    });

    // Add totals row
    const totals = [
      '', 'TOTAL', '', '', '',
      '',
      '',
      computedAllocations.reduce((s, a) => s + a.currentSalary, 0),
      computedAllocations.reduce((s, a) => s + a.currentSalary, 0) * 12,
      '',
      '',
      totalAllocated,
      computedAllocations.reduce((s, a) => s + a.newSalary, 0),
      computedAllocations.reduce((s, a) => s + a.newSalary, 0) * 12,
    ];
    rows.push(totals);

    // Build CSV
    const escapeCSV = (val) => {
      const str = String(val == null ? '' : val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return '"' + str.replace(/"/g, '""') + '"';
      }
      return str;
    };

    const csv = [headers.map(escapeCSV).join(','), ...rows.map(r => r.map(escapeCSV).join(','))].join('\n');

    // Trigger download
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const today = new Date().toISOString().slice(0, 10);
    link.download = `Salary_Revision_Sheet_${today}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Compute derived values
  const computedAllocations = allocations.map(a => {
    const incrementAmount = Math.round((a.currentSalary * a.suggestedPercent) / 100);
    const newSalary = a.currentSalary + incrementAmount;
    return { ...a, incrementAmount, newSalary };
  });

  const totalAllocated = computedAllocations.reduce((sum, a) => sum + a.incrementAmount, 0);
  const remainingBudget = budget ? budget - totalAllocated : 0;
  const utilizationPercentage = budget ? ((totalAllocated / budget) * 100).toFixed(1) : '0.0';
  const isOverBudget = remainingBudget < 0;

  const styles = {
    container: { display: 'flex', flexDirection: 'column', gap: '24px' },
    card: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
    sectionTitle: { fontSize: '16px', fontWeight: '700', color: '#1a1a2e', marginBottom: '20px' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' },
    label: { fontSize: '13px', fontWeight: '600', color: '#1a1a2e' },
    input: { padding: '12px 16px', borderRadius: '6px', border: '1px solid #e0e0e0', fontSize: '14px', fontFamily: 'inherit' },
    settingsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '24px' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginTop: '24px' },
    statBox: { backgroundColor: '#f9f9f9', padding: '16px', borderRadius: '8px', border: '1px solid #f0f0f0' },
    statLabel: { fontSize: '12px', fontWeight: '600', color: '#999', textTransform: 'uppercase', marginBottom: '8px' },
    statValue: { fontSize: '24px', fontWeight: '700', color: '#1a1a2e' },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: '12px' },
    tableHeader: { backgroundColor: '#f9f9f9', borderBottom: '2px solid #e0e0e0' },
    tableHeaderCell: { padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#1a1a2e', textTransform: 'uppercase' },
    tableRow: { borderBottom: '1px solid #f0f0f0' },
    tableCell: { padding: '12px', fontSize: '13px', color: '#666' },
    gaugeBar: { height: '24px', borderRadius: '12px', backgroundColor: '#e0e0e0', overflow: 'hidden', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)' },
    gaugeFill: { height: '100%', transition: 'width 0.3s ease', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '8px', color: 'white', fontWeight: '700', fontSize: '11px' },
    inlineInput: { width: '60px', padding: '4px 6px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '13px', textAlign: 'center', fontFamily: 'inherit' },
    ratingBadge: { display: 'inline-block', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' },
  };

  const getRatingBadgeColor = (rating) => {
    if (rating >= 4.5) return { bg: '#e8f5e9', color: '#2e7d32' };
    if (rating >= 4.0) return { bg: '#e3f2fd', color: '#1565c0' };
    if (rating >= 3.5) return { bg: '#fff3e0', color: '#e65100' };
    return { bg: '#ffebee', color: '#c62828' };
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.sectionTitle}>Loading Budget Manager...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.sectionTitle}>Error Loading Budget Manager</div>
          <div style={{ color: '#c62828', fontSize: '14px', padding: '12px', backgroundColor: '#ffebee', borderRadius: '6px', borderLeft: '4px solid #c62828' }}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!budget || maxCapPercentage === null) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.sectionTitle}>Loading Budget Configuration...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Budget Configuration */}
      <div style={styles.card}>
        <div style={styles.sectionTitle}>Budget Configuration</div>
        <div style={styles.settingsGrid}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Total Annual Budget (INR)</label>
            <input
              type="number"
              style={styles.input}
              value={budget}
              onChange={(e) => handleBudgetChange(Number(e.target.value))}
            />
            <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
              ₹ {budget.toLocaleString('en-IN')}
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Max Cap Per Employee (%)</label>
            <input
              type="number"
              style={styles.input}
              min="1"
              max="100"
              value={maxCapPercentage}
              onChange={(e) => handleMaxCapChange(Number(e.target.value))}
            />
            <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
              Suggested % for any employee cannot exceed {maxCapPercentage}%
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div style={styles.statsGrid}>
          <div style={styles.statBox}>
            <div style={styles.statLabel}>Total Employees</div>
            <div style={styles.statValue}>{computedAllocations.length}</div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statLabel}>Total Allocated</div>
            <div style={{ ...styles.statValue, color: isOverBudget ? '#f44336' : '#8bc34a' }}>
              ₹ {totalAllocated.toLocaleString('en-IN')}
            </div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statLabel}>Remaining Budget</div>
            <div style={{ ...styles.statValue, color: isOverBudget ? '#f44336' : '#2e7d32' }}>
              {isOverBudget ? '- ' : ''}₹ {Math.abs(remainingBudget).toLocaleString('en-IN')}
            </div>
            {isOverBudget && (
              <div style={{ fontSize: '11px', color: '#f44336', marginTop: '4px', fontWeight: '600' }}>
                OVER BUDGET — reduce suggested % for some employees
              </div>
            )}
          </div>
          <div style={styles.statBox}>
            <div style={styles.statLabel}>Budget Utilization</div>
            <div style={{ ...styles.statValue, color: utilizationPercentage > 100 ? '#f44336' : '#1a1a2e' }}>
              {utilizationPercentage}%
            </div>
          </div>
        </div>

        {/* Budget Gauge */}
        <div style={{ marginTop: '16px' }}>
          <div style={{ fontSize: '12px', fontWeight: '600', color: '#666', marginBottom: '8px' }}>
            Budget Utilization Gauge
          </div>
          <div style={styles.gaugeBar}>
            <div style={{
              ...styles.gaugeFill,
              width: `${Math.min(utilizationPercentage, 100)}%`,
              backgroundColor: utilizationPercentage > 100 ? '#f44336' : utilizationPercentage > 90 ? '#ff9800' : '#8bc34a',
            }}>
              {utilizationPercentage}%
            </div>
          </div>
        </div>
      </div>

      {/* Employee Allocation Table */}
      <div style={styles.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a2e' }}>Budget Allocation by Employee</div>
          <button
            onClick={downloadSalarySheet}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 16px', backgroundColor: '#0f3460', color: 'white',
              border: 'none', borderRadius: '6px', cursor: 'pointer',
              fontSize: '12px', fontWeight: '600',
            }}
            onMouseEnter={e => { e.target.style.backgroundColor = '#e94560'; }}
            onMouseLeave={e => { e.target.style.backgroundColor = '#0f3460'; }}
          >
            ⬇ Download Salary Revision Sheet
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead style={styles.tableHeader}>
              <tr>
                <th style={styles.tableHeaderCell}>Employee Name</th>
                <th style={styles.tableHeaderCell}>Band</th>
                <th style={styles.tableHeaderCell}>Current Salary</th>
                <th style={{ ...styles.tableHeaderCell, textAlign: 'center' }}>Rating</th>
                <th style={{ ...styles.tableHeaderCell, textAlign: 'center' }}>Grid %</th>
                <th style={{ ...styles.tableHeaderCell, textAlign: 'center' }}>Suggested %</th>
                <th style={styles.tableHeaderCell}>Increment Amount</th>
                <th style={styles.tableHeaderCell}>New Salary</th>
              </tr>
            </thead>
            <tbody>
              {computedAllocations.map((alloc, idx) => {
                const ratingColor = getRatingBadgeColor(alloc.rating);
                return (
                  <tr
                    key={alloc.id}
                    style={{ ...styles.tableRow, backgroundColor: idx % 2 === 0 ? '#fff' : '#f9f9f9' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0f8ff'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#fff' : '#f9f9f9'; }}
                  >
                    <td style={styles.tableCell}>
                      <div style={{ fontWeight: '600', color: '#1a1a2e' }}>{alloc.name}</div>
                      <div style={{ fontSize: '11px', color: '#999' }}>{alloc.designation}</div>
                    </td>
                    <td style={styles.tableCell}>
                      <div style={{ fontSize: '12px' }}>{alloc.band || '—'}</div>
                    </td>
                    <td style={styles.tableCell}>₹ {alloc.currentSalary.toLocaleString('en-IN')}</td>
                    <td style={{ ...styles.tableCell, textAlign: 'center' }}>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="5"
                        value={alloc.rating}
                        onChange={(e) => handleRatingChange(alloc.id, e.target.value)}
                        style={{
                          ...styles.inlineInput,
                          backgroundColor: ratingColor.bg,
                          color: ratingColor.color,
                          fontWeight: '700',
                          border: `1px solid ${ratingColor.color}40`,
                        }}
                      />
                    </td>
                    <td style={{ ...styles.tableCell, textAlign: 'center' }}>
                      <span style={{
                        fontSize: '12px', fontWeight: '600',
                        color: alloc.gridPercent > 0 ? '#0f3460' : '#999',
                      }}>
                        {alloc.gridPercent > 0 ? `${alloc.gridPercent}%` : '—'}
                      </span>
                    </td>
                    <td style={{ ...styles.tableCell, textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px' }}>
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          max={maxCapPercentage}
                          value={alloc.suggestedPercent}
                          onChange={(e) => handleSuggestedPercentChange(alloc.id, e.target.value)}
                          style={{
                            ...styles.inlineInput,
                            width: '55px',
                            backgroundColor: alloc.suggestedPercent >= maxCapPercentage ? '#fff3e0' : '#fff',
                            borderColor: alloc.suggestedPercent >= maxCapPercentage ? '#e65100' : '#ddd',
                          }}
                        />
                        <span style={{ fontSize: '12px', color: '#999' }}>%</span>
                      </div>
                    </td>
                    <td style={styles.tableCell}>
                      <div style={{ fontWeight: '600', color: '#8bc34a' }}>
                        ₹ {alloc.incrementAmount.toLocaleString('en-IN')}
                      </div>
                    </td>
                    <td style={styles.tableCell}>
                      <div style={{ fontWeight: '600', color: '#1a1a2e' }}>
                        ₹ {alloc.newSalary.toLocaleString('en-IN')}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ backgroundColor: '#f0f0f0', borderTop: '2px solid #ccc' }}>
                <td colSpan={6} style={{ ...styles.tableCell, fontWeight: '700', color: '#1a1a2e', textAlign: 'right' }}>
                  TOTALS
                </td>
                <td style={{ ...styles.tableCell, fontWeight: '700', color: isOverBudget ? '#f44336' : '#8bc34a' }}>
                  ₹ {totalAllocated.toLocaleString('en-IN')}
                </td>
                <td style={{ ...styles.tableCell, fontWeight: '700', color: '#1a1a2e' }}>
                  ₹ {computedAllocations.reduce((s, a) => s + a.newSalary, 0).toLocaleString('en-IN')}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Increment Guidelines */}
      <div style={styles.card}>
        <div style={styles.sectionTitle}>Appraisal Grid Reference</div>
        <div style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
          Suggested % is auto-calculated from the <strong>GRID Sheet → Appraisal Grid</strong> tab based on employee's Band, Salary Slab, and Rating Score Range.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          {[
            { label: 'Rating 4.5–5.0', range: '93-100', color: '#2e7d32' },
            { label: 'Rating 3.5–4.4', range: '88-92', color: '#1565c0' },
            { label: 'Rating 2.5–3.4', range: '82-87', color: '#e65100' },
            { label: 'Rating 1.5–2.4', range: '76-81', color: '#c62828' },
            { label: 'Rating < 1.5', range: '51-75', color: '#666' },
          ].map(g => (
            <div key={g.label} style={{ paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ fontWeight: '600', color: g.color, marginBottom: '4px' }}>{g.label}</div>
              <div style={{ fontSize: '13px', color: '#666' }}>Score Range: {g.range} (max cap {maxCapPercentage}%)</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BudgetManager;
